import React from 'react';
import { Input } from '@/components/ui/input';
import { Conversation } from '@/services/messageService';
import { format, isToday, isYesterday } from 'date-fns';
import { useResponsive } from '@/hooks/useResponsive';
import { RESPONSIVE_SPACING, RESPONSIVE_TYPOGRAPHY, TOUCH_TARGETS } from '@/design-tokens/responsive';
import { cn } from '@/lib/utils';

interface ConversationListPanelProps {
  conversations: Conversation[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  loading: boolean;
  currentConversationId?: string;
  onSelectConversation: (id: string) => void;
}

const ConversationListPanel: React.FC<ConversationListPanelProps> = ({
  conversations,
  searchTerm,
  setSearchTerm,
  loading,
  currentConversationId,
  onSelectConversation
}) => {
  const { isMobile } = useResponsive();
  // Helper to format last message time
  const getLastMessageTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isToday(date)) return format(date, 'h:mm a');
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d, yyyy');
  };

  return (
    <div className="h-full flex flex-col border rounded-2xl overflow-hidden bg-white shadow-sm">
      <div className={cn(
        "border-b bg-gray-50/50",
        RESPONSIVE_SPACING.card.padding
      )}>
        <Input 
          placeholder="Search conversations..." 
          className={cn(
            "px-3 rounded-xl border-gray-200 focus:border-primary focus:ring-primary/20",
            isMobile && "text-base" // Prevent zoom on iOS
          )}
          style={{ minHeight: TOUCH_TARGETS.minSize }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className={cn(
        "flex-1 overflow-y-auto space-y-2",
        RESPONSIVE_SPACING.section.mobile
      )}>
        {loading ? (
          <div className="flex justify-center p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className={cn(
            "text-center text-gray-500",
            RESPONSIVE_SPACING.section.mobile,
            "py-8"
          )}>
            <p className={RESPONSIVE_TYPOGRAPHY.body}>
              {searchTerm ? 'No conversations match your search' : 'No conversations yet'}
            </p>
          </div>
        ) : (
          conversations.map((conv) => {
            const isUnread = conv.unreadCount > 0;
            return (
              <button
                key={conv.id}
                onClick={() => onSelectConversation(conv.id)}
                className={cn(
                  "w-full text-left rounded-xl hover:bg-gray-50 transition-all duration-200 flex flex-col relative",
                  "border border-transparent hover:border-gray-200 hover:shadow-sm",
                  RESPONSIVE_SPACING.card.padding,
                  conv.id === currentConversationId && "bg-primary/5 border-primary/20 shadow-sm",
                  isUnread && conv.id !== currentConversationId && "bg-blue-50/50 border-blue-100"
                )}
                style={{ minHeight: TOUCH_TARGETS.comfortable }}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={cn(
                    "truncate",
                    RESPONSIVE_TYPOGRAPHY.bodyLarge,
                    isUnread ? 'font-bold text-primary' : 'font-medium text-gray-900'
                  )}> 
                    {conv.title || 'Untitled'}
                  </span>
                  <span className={cn(
                    "text-gray-400 ml-2 whitespace-nowrap",
                    RESPONSIVE_TYPOGRAPHY.caption
                  )}>
                    {getLastMessageTime(conv.last_message_at || conv.updated_at || conv.created_at)}
                  </span>
                </div>
                {/* Last message preview */}
                {conv.lastMessage && (
                  <p className={cn(
                    "text-gray-600 truncate mb-1",
                    RESPONSIVE_TYPOGRAPHY.bodySmall
                  )}>
                    {conv.lastMessage.sender_type === 'parent' ? 'You: ' : ''}
                    {conv.lastMessage.content.length > 48
                      ? conv.lastMessage.content.slice(0, 48) + '…'
                      : conv.lastMessage.content}
                  </p>
                )}
                {/* Organization name - who the parent is talking with */}
                <p className={cn(
                  "text-gray-400 truncate",
                  RESPONSIVE_TYPOGRAPHY.caption
                )}>
                  {conv.organization?.name || 'Studio'}
                </p>
                {/* Unread badge */}
                {isUnread && (
                  <span className="absolute top-3 right-3 inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-bold bg-primary text-white shadow-sm min-w-[20px] h-5">
                    {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationListPanel;
